import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { money, WEEK_ORDER, WEEK_LABELS, hm12 } from "@/lib/format";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Scissors, MapPin, Phone, Mail, Instagram, Facebook, Star, Clock, ChevronRight, Award, Menu, X, Share2, MessageCircle, Images, Sparkles, Camera, ScanFace, Wand2 } from "lucide-react";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.5 3.4 3.5 3.6v2.4c-1.2.1-2.4-.2-3.5-.8v5.9c0 3.4-2.5 5.9-5.9 5.9-1.9 0-3.6-.9-4.6-2.4-1.6-2.4-.8-5.8 1.8-7.1 1-.5 2-.6 3-.5v2.5c-.5-.1-1-.1-1.5.1-1.2.4-1.8 1.6-1.4 2.8.4 1.2 1.7 1.8 2.9 1.4.9-.3 1.5-1.2 1.5-2.2V3h2.7z" />
  </svg>
);

const HERO = "https://images.unsplash.com/photo-1536520002442-39764a41e987?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const AI_STEPS = [
  { icon: Camera, t: "Foto de frente y de lado", d: "En sucursal, tu barbero te toma una foto." },
  { icon: ScanFace, t: "Detectamos tu rostro y cabello", d: "Ovalado, redondo, cuadrado, corazón… y tu tipo de cabello." },
  { icon: Wand2, t: "4 cortes recomendados", d: "Ves cómo te quedarían antes de cortarte, o eliges otro estilo." },
];

const NakamaIASection = () => (
  <section id="nakama-ia" className="max-w-6xl mx-auto px-5 pt-4 pb-24" data-testid="nakama-ia-section">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className="order-2 lg:order-1 max-w-sm mx-auto w-full">
        <BeforeAfterSlider before="/nakama-ia/demo-before.jpg" after="/nakama-ia/demo-after.jpg" testid="landing-ai-slider" />
        <p className="text-xs text-[#737373] text-center mt-3">Imagen de demostración generada con IA. Desliza para comparar.</p>
      </div>
      <div className="order-1 lg:order-2">
        <p className="text-[#d4af37] text-sm uppercase tracking-widest mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" />Nuevo · Servicio plus</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Nakama IA · Simulador de cortes</h2>
        <p className="text-[#a3a3a3] mb-8">Antes de cortarte, mira cómo te quedaría. En sucursal podrás probar el servicio de Nakama IA con tu barbero.</p>
        <div className="space-y-5 mb-8">
          {AI_STEPS.map((s, i) => (
            <div key={s.t} className="flex gap-4" data-testid={`ai-step-${i}`}>
              <div className="w-11 h-11 rounded-full border border-[#d4af37]/40 flex items-center justify-center shrink-0"><s.icon className="w-5 h-5 text-[#d4af37]" /></div>
              <div><p className="font-bold">{s.t}</p><p className="text-sm text-[#a3a3a3]">{s.d}</p></div>
            </div>
          ))}
        </div>
        <Link to="/book" className="nk-btn px-8 py-3 inline-flex items-center gap-2" data-testid="ai-book-btn">Agendar cita <ChevronRight className="w-4 h-4" /></Link>
        <p className="text-xs text-[#737373] mt-5" data-testid="ai-landing-disclaimer">La simulación es una referencia visual generada con inteligencia artificial y no garantiza el resultado real del corte.</p>
      </div>
    </div>
  </section>
);

function renderHeroTitle(title, hl) {
  if (!hl || !title.includes(hl)) return title;
  const idx = title.indexOf(hl);
  return (<>{title.slice(0, idx)}<span className="text-[#d4af37]">{hl}</span>{title.slice(idx + hl.length)}</>);
}

// Si la app instalada (PWA) se recarga sola, regresa al barbero a donde estaba
const useResumeStaff = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
    if (!standalone || !localStorage.getItem("nakama_token")) return;
    try {
      const last = JSON.parse(localStorage.getItem("nk_last_staff") || "null");
      if (last?.p?.startsWith("/barber") && Date.now() - last.t < 2 * 3600 * 1000) navigate(last.p, { replace: true });
    } catch { /* noop */ }
  }, [navigate]);
};

export default function Landing() {
  useResumeStaff();
  const [cfg, setCfg] = useState({});
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [menu, setMenu] = useState(false);
  const [cfgLoaded, setCfgLoaded] = useState(false);

  useEffect(() => {
    api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {}).finally(() => setCfgLoaded(true));
    api.get("/public/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/public/barbers").then((r) => setBarbers(r.data)).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/public/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const share = async () => {
    const shareData = {
      title: cfg.business_name || "Barbería Nakama",
      text: "Reserva tu cita en Barbería Nakama — Mérida, Yucatán ✂️",
      url: window.location.origin,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (e) { /* cancelado */ }
    } else {
      try { await navigator.clipboard.writeText(shareData.url); toast.success("Enlace copiado al portapapeles"); }
      catch { window.open(`https://wa.me/?text=${encodeURIComponent(shareData.text + " " + shareData.url)}`, "_blank"); }
    }
  };

  const waBusiness = () => {
    const num = (cfg.whatsapp || "").replace(/\D/g, "");
    return `https://wa.me/${num}?text=${encodeURIComponent("Hola, quiero información / agendar una cita ✂️")}`;
  };

  return (
    <div className="bg-[#0f0f0f] text-[#f5f5f5] min-h-screen">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0f0f0f]/70 border-b border-[#2a2a2a]">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scissors className="w-6 h-6 text-[#d4af37]" />
            <span className="font-extrabold tracking-wide">NAKAMA</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#a3a3a3]">
            <a href="#servicios" className="hover:text-[#f5f5f5] transition-colors">Servicios</a>
            <a href="#barberos" className="hover:text-[#f5f5f5] transition-colors">Barberos</a>
            <a href="#galeria" className="hover:text-[#f5f5f5] transition-colors">Galería</a>
            <a href="#resenas" className="hover:text-[#f5f5f5] transition-colors">Reseñas</a>
            <a href="#ubicacion" className="hover:text-[#f5f5f5] transition-colors">Ubicación</a>
            <Link to="/wallet" className="hover:text-[#f5f5f5] transition-colors">Mis puntos</Link>
            <Link to="/login" data-testid="nav-acceso-personal" className="hover:text-[#d4af37] transition-colors">Acceso personal</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/book" data-testid="nav-reservar" className="nk-btn text-sm hidden sm:inline-block">Reservar cita</Link>
            <button className="md:hidden p-2" onClick={() => setMenu(!menu)}>{menu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          </div>
        </div>
        {menu && (
          <div className="md:hidden border-t border-[#2a2a2a] px-5 py-4 flex flex-col gap-3 text-sm bg-[#0f0f0f]">
            <a href="#servicios" onClick={() => setMenu(false)}>Servicios</a>
            <a href="#barberos" onClick={() => setMenu(false)}>Barberos</a>
            <a href="#galeria" onClick={() => setMenu(false)}>Galería</a>
            <a href="#resenas" onClick={() => setMenu(false)}>Reseñas</a>
            <Link to="/wallet">Mis puntos</Link>
            <Link to="/login" data-testid="nav-acceso-personal-mobile" className="text-[#d4af37]">Acceso personal</Link>
            <Link to="/book" className="nk-btn text-center">Reservar cita</Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center">
        <img src={HERO} alt="Barbería" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f0f] via-[#0f0f0f]/85 to-[#0f0f0f]/40" />
        <div className="relative max-w-6xl mx-auto px-5 pt-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: cfgLoaded ? 1 : 0, y: cfgLoaded ? 0 : 20 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] border border-[#d4af37]/40 rounded-full px-4 py-1.5 mb-6">
              <Award className="w-3.5 h-3.5" /> {cfg.hero_badge || "Barbería Nakama en Mérida"}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-testid="hero-title">
              {renderHeroTitle(cfg.hero_title || "Tu mejor estilo, a la altura que mereces.", cfg.hero_highlight || "a la altura")}
            </h1>
            <p className="text-[#a3a3a3] text-lg leading-relaxed mb-8 max-w-lg" data-testid="hero-subtitle">
              {cfg.hero_subtitle || "Cortes de precisión, afeitado clásico y atención de primer nivel en el corazón de Mérida, Yucatán."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-reservar" className="nk-btn text-base px-8 py-3.5 flex items-center gap-2">
                Reservar cita <ChevronRight className="w-4 h-4" />
              </Link>
              <a href="#servicios" className="nk-btn-ghost text-base px-8 py-3.5">Ver servicios</a>
              <button onClick={share} data-testid="hero-share" className="nk-btn-ghost text-base px-6 py-3.5 flex items-center gap-2">
                <Share2 className="w-4 h-4" /> Compartir
              </button>
              {cfg.google_maps_url && <a href={cfg.google_maps_url} target="_blank" rel="noreferrer" data-testid="hero-como-llegar" className="nk-btn-ghost text-base px-6 py-3.5 flex items-center gap-2"><MapPin className="w-4 h-4" />Cómo llegar</a>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="servicios" className="max-w-6xl mx-auto px-5 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[#d4af37] text-sm uppercase tracking-widest mb-2">{cfg.sec_services_eyebrow || "Nuestros servicios"}</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold">{cfg.sec_services_title || "Servicios & precios"}</h2>
          </div>
          <Link to="/book" className="hidden sm:flex items-center gap-1 text-[#d4af37] text-sm hover:gap-2 transition-all">Reservar <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="nk-card overflow-hidden group hover:border-[#d4af37]/50 transition-colors" data-testid={`service-card-${s.id}`}>
              {s.image_url && <div className="h-44 overflow-hidden"><img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg">{s.name}</h3>
                  <span className="text-[#d4af37] font-bold">{money(s.price)}</span>
                </div>
                <p className="text-[#a3a3a3] text-sm mb-3 leading-relaxed">{s.description}</p>
                <span className="inline-flex items-center gap-1.5 text-xs text-[#737373]"><Clock className="w-3.5 h-3.5" /> {s.duration_min} min</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <NakamaIASection />

      {/* Barbers */}
      <section id="barberos" className="bg-[#111111] border-y border-[#2a2a2a] py-24">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-[#d4af37] text-sm uppercase tracking-widest mb-2">{cfg.sec_barbers_eyebrow || "El equipo"}</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-12">{cfg.sec_barbers_title || "Nuestros barberos"}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {barbers.map((b) => (
              <div key={b.id} className="nk-card overflow-hidden group" data-testid={`barber-card-${b.id}`}>
                <div className="h-64 overflow-hidden bg-[#242424]">
                  {b.photo_url && <img src={b.photo_url} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg" style={{ color: b.color || "#f5f5f5" }}>{b.name}</h3>
                  <p className="text-[#a3a3a3] text-sm mt-1">{b.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section id="galeria" className="max-w-6xl mx-auto px-5 py-24">
          <p className="text-[#d4af37] text-sm uppercase tracking-widest mb-2">{cfg.sec_gallery_eyebrow || "Nuestro trabajo"}</p>
          <div className="flex items-end justify-between mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold flex items-center gap-3"><Images className="w-8 h-8 text-[#d4af37]" />{cfg.sec_gallery_title || "Galería de cortes"}</h2>
          </div>
          <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
            {gallery.map((g) => (
              <div key={g.id} data-testid={`gallery-${g.id}`} className="mb-4 break-inside-avoid overflow-hidden rounded-[14px] border border-[#2a2a2a] group">
                <img src={g.image_url} alt={g.caption || "Corte Nakama"} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {g.caption && <p className="text-xs text-[#a3a3a3] px-3 py-2">{g.caption}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section id="resenas" className="max-w-6xl mx-auto px-5 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[#d4af37] text-sm uppercase tracking-widest mb-2">{cfg.sec_reviews_eyebrow || "Testimonios"}</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold">{cfg.sec_reviews_title || "Lo que dicen nuestros clientes"}</h2>
          </div>
          <Link to="/testimonial" className="text-[#d4af37] text-sm hover:underline" data-testid="leave-review">Dejar reseña</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.id} className="nk-card p-6" data-testid={`testimonial-${t.id}`}>
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < t.rating ? "text-[#d4af37] fill-[#d4af37]" : "text-[#2a2a2a]"}`} />)}
              </div>
              <p className="text-[#f5f5f5] leading-relaxed mb-4">“{t.comment}”</p>
              <p className="text-sm text-[#737373] font-medium">— {t.name}</p>
            </div>
          ))}
          {testimonials.length === 0 && <p className="text-[#737373]">Aún no hay reseñas publicadas.</p>}
        </div>
      </section>

      {/* Location / CTA */}
      <section id="ubicacion" className="bg-[#111111] border-t border-[#2a2a2a] py-24">
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#d4af37] text-sm uppercase tracking-widest mb-2">{cfg.sec_location_eyebrow || "Visítanos"}</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-6">{cfg.sec_location_title || "Ubicación & contacto"}</h2>
            <div className="space-y-4 text-[#a3a3a3]">
              <p className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#d4af37] shrink-0 mt-0.5" /> {cfg.address}</p>
              <p className="flex items-center gap-3"><Phone className="w-5 h-5 text-[#d4af37]" /> {cfg.phone}</p>
              {cfg.email && <p className="flex items-center gap-3"><Mail className="w-5 h-5 text-[#d4af37]" /> {cfg.email}</p>}
            </div>
            <div className="flex items-center gap-3 mt-6">
              {cfg.instagram && <a href={cfg.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" title="Instagram" className="w-11 h-11 rounded-full border border-[#2a2a2a] flex items-center justify-center text-[#a3a3a3] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors"><Instagram className="w-5 h-5" /></a>}
              {cfg.facebook && <a href={cfg.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" title="Facebook" className="w-11 h-11 rounded-full border border-[#2a2a2a] flex items-center justify-center text-[#a3a3a3] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors"><Facebook className="w-5 h-5" /></a>}
              {cfg.tiktok && <a href={cfg.tiktok} target="_blank" rel="noreferrer" data-testid="social-tiktok" title="TikTok" className="w-11 h-11 rounded-full border border-[#2a2a2a] flex items-center justify-center text-[#a3a3a3] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors"><TikTokIcon className="w-5 h-5" /></a>}
              {cfg.whatsapp && <a href={waBusiness()} target="_blank" rel="noreferrer" data-testid="social-whatsapp" title="WhatsApp Business" className="w-11 h-11 rounded-full border border-[#16a34a]/40 flex items-center justify-center text-[#16a34a] hover:bg-[#16a34a]/10 transition-colors"><MessageCircle className="w-5 h-5" /></a>}
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/book" className="nk-btn px-8 py-3">Reservar ahora</Link>
              {cfg.google_maps_url && <a href={cfg.google_maps_url} target="_blank" rel="noreferrer" data-testid="como-llegar" className="nk-btn-ghost px-8 py-3 flex items-center gap-2"><MapPin className="w-4 h-4" />Cómo llegar</a>}
              <button onClick={share} data-testid="location-share" className="nk-btn-ghost px-8 py-3 flex items-center gap-2"><Share2 className="w-4 h-4" />Compartir</button>
            </div>
            <div className="nk-card p-6 mt-8" data-testid="hours-card">
              <h3 className="font-bold flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-[#d4af37]" />Horario de atención</h3>
              <div className="space-y-0">
                {WEEK_ORDER.map((d) => {
                  const h = (cfg.default_hours || {})[d] || {};
                  const isToday = new Date().getDay() === (d === "sun" ? 0 : WEEK_ORDER.indexOf(d) + 1);
                  return (
                    <div key={d} className={`flex justify-between text-sm py-2 border-b border-[#2a2a2a]/60 last:border-0 ${isToday ? "text-[#d4af37] font-semibold" : ""}`}>
                      <span className={isToday ? "" : "text-[#a3a3a3]"}>{WEEK_LABELS[d]}{isToday ? " · hoy" : ""}</span>
                      {h.closed ? <span className="text-[#737373]">Cerrado</span> : <span>{hm12(h.open)} – {hm12(h.close)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="nk-card p-8 text-center">
            <Scissors className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
            <h3 className="text-2xl font-extrabold mb-2">{cfg.business_name || "Barbería Nakama"}</h3>
            <p className="text-[#a3a3a3]">Acumula puntos en cada visita y canjéalos por descuentos con nuestro programa de lealtad.</p>
            <Link to="/wallet" className="inline-block mt-6 text-[#d4af37] font-semibold hover:underline">Consultar mis puntos →</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2a2a2a] py-8 text-center text-sm text-[#737373]">
        <p>© {new Date().getFullYear()} {cfg.business_name || "Barbería Nakama"} · Mérida, Yucatán</p>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs">
          <Link to="/aviso-de-privacidad" data-testid="footer-privacy" className="hover:text-[#d4af37] transition-colors">Aviso de Privacidad</Link>
          <span className="text-[#2a2a2a]">·</span>
          <Link to="/terminos" data-testid="footer-terms" className="hover:text-[#d4af37] transition-colors">Términos y Condiciones</Link>
        </div>
        <Link to="/login" data-testid="footer-acceso-personal" className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors font-medium">
          <Scissors className="w-4 h-4" /> Acceso personal
        </Link>
      </footer>
    </div>
  );
}
